import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { GripVertical } from 'lucide-react';
import { useState } from 'react';
import { Checkbox } from '@/components/shadcn-ui/checkbox';
import type { Goal } from '@/entities/goal';
import { GoalItem } from './GoalItem';

interface GoalsListDisplayProps {
  goals: Goal[];
  parentTitleMap: Map<string, string>;
  focusGoalId?: string;
  focusCursorPosition?: number;
  onGoalFocused?: (goalId: string) => void;
  period: string;
  startAtLocal: string;
  plannedGoalPositions: Map<string, number>;
  showPlaceholder?: boolean;
  onToggle: (checked: boolean, goalId: string) => void;
  onInsertGoal: (
    insertAtPosition: number,
    newGoalTitle: string,
    options?: { parentGoalId?: string; focusCurrentGoalId?: string },
  ) => void;
  onBackspaceAtStart: (goalId: string, currentTitle: string) => void;
  onDeleteAtEnd: (goalId: string, currentTitle: string) => void;
  onFocusNext: (currentGoalId: string, cursorPosition: number) => void;
  onFocusPrevious: (currentGoalId: string, cursorPosition: number) => void;
  onMove: (goalId: string, direction: 'up' | 'down') => void;
  onPasteGoals: (
    insertAtPosition: number,
    titles: string[],
    parentGoalId?: string,
  ) => void;
  onDropGoal: (goalId: string, newIndex: number) => void;
}

export function GoalsListDisplay({
  goals,
  parentTitleMap,
  focusGoalId,
  focusCursorPosition,
  period,
  startAtLocal,
  plannedGoalPositions,
  showPlaceholder = false,
  onToggle,
  onInsertGoal,
  onBackspaceAtStart,
  onDeleteAtEnd,
  onFocusNext,
  onFocusPrevious,
  onMove,
  onPasteGoals,
  onDropGoal,
  onGoalFocused = () => {},
}: GoalsListDisplayProps) {
  const [activeGoalId, setActiveGoalId] = useState<string | null>(null);
  const [overGoalId, setOverGoalId] = useState<string | null>(null);

  const activeGoal = goals.find((g) => g.id === activeGoalId) ?? null;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const goalIds = goals.map((g) => g.id);

  function handleDragStart(event: DragStartEvent) {
    setActiveGoalId(String(event.active.id));
  }

  function handleDragOver(event: { over: { id: string | number } | null }) {
    setOverGoalId(event.over ? String(event.over.id) : null);
  }

  function handleDragCancel() {
    setActiveGoalId(null);
    setOverGoalId(null);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveGoalId(null);
    setOverGoalId(null);

    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const goalId = String(active.id);
    const overIndex = goalIds.indexOf(String(over.id));
    if (overIndex === -1) {
      return;
    }

    onDropGoal(goalId, overIndex);
  }

  const activeIndex = activeGoalId ? goalIds.indexOf(activeGoalId) : -1;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={goalIds} strategy={verticalListSortingStrategy}>
        <ul className="space-y-2">
          {goals.map((goal, index) => {
            const isOver = overGoalId === goal.id && activeGoalId !== goal.id;
            const showLineAbove =
              isOver && activeIndex !== -1 && activeIndex > index;
            const showLineBelow =
              isOver && activeIndex !== -1 && activeIndex < index;

            return (
              <GoalItem
                key={goal.id}
                index={index}
                goal={goal}
                parentGoalTitle={
                  goal.parentGoalId
                    ? parentTitleMap.get(goal.parentGoalId)
                    : undefined
                }
                period={period}
                startAtLocal={startAtLocal}
                position={plannedGoalPositions.get(goal.id) ?? index}
                showPlaceholder={showPlaceholder}
                onToggle={onToggle}
                onInsertGoal={onInsertGoal}
                onBackspaceAtStart={onBackspaceAtStart}
                onDeleteAtEnd={onDeleteAtEnd}
                onFocusNext={onFocusNext}
                onFocusPrevious={onFocusPrevious}
                onMove={onMove}
                onPasteGoals={onPasteGoals}
                dropIndicator={
                  showLineAbove ? 'above' : showLineBelow ? 'below' : undefined
                }
                autoFocus={focusGoalId === goal.id}
                focusCursorPosition={
                  focusGoalId === goal.id ? focusCursorPosition : undefined
                }
                onFocused={onGoalFocused}
              />
            );
          })}
        </ul>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeGoal ? (
          <ul className="opacity-50">
            <li className="relative flex items-start gap-2">
              <button
                type="button"
                className="absolute top-[2.5px] -left-6"
                aria-hidden
              >
                <GripVertical className="h-4 w-4 text-gray-400" />
              </button>
              <Checkbox
                checked={activeGoal.status === 'done'}
                className="pointer-events-none mt-[2.5px]"
              />
              <span className="text-sm font-light">{activeGoal.title}</span>
            </li>
          </ul>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
